import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { clearRequests, fetchMockRequests, mockKeys } from "./mock-queries";
import { type MockRequest } from "../../../shared/types";
import { Badge, methodTone, statusTone } from "../../components/Badge";
import { Button } from "../../components/Button";
import { EmptyState, ErrorBanner, Loading } from "../../components/Feedback";
import { formatDateTime, formatRelative, prettyJson } from "../../lib/format";

type RequestsPanelProps = {
  /** Mock whose requests to show */
  tokenUuid: string;
  /** Whether the user may clear them */
  canClear: boolean;
};

type RequestDetailProps = {
  /** Request to show */
  request: MockRequest;
};

const RequestDetail = ({ request }: RequestDetailProps) => (
  <div className="grid gap-4 bg-slate-50 px-4 py-3 lg:grid-cols-2">
    <div>
      <p className="label">Headers</p>
      <dl className="max-h-72 overflow-auto rounded border border-slate-200 bg-white p-2 font-mono text-xs">
        {Object.entries(request.headers)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([name, value]) => (
            <div key={name} className="flex gap-2 py-0.5">
              <dt className="shrink-0 text-slate-500">{name}:</dt>
              <dd className="break-all">{value}</dd>
            </div>
          ))}
      </dl>
      {request.queryString ? (
        <>
          <p className="label mt-3">Query</p>
          <pre className="overflow-auto rounded border border-slate-200 bg-white p-2 font-mono text-xs">
            {request.queryString}
          </pre>
        </>
      ) : null}
    </div>
    <div>
      <p className="label">
        Body{request.bodyIsBase64 ? " (base64)" : ""}
        {request.bodyTruncated ? " (truncated at 256 KB)" : ""}
      </p>
      <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-all rounded border border-slate-200 bg-white p-2 font-mono text-xs">
        {request.body ? (
          request.bodyIsBase64 ? (
            request.body
          ) : (
            prettyJson(request.body)
          )
        ) : (
          <span className="text-slate-400">(empty)</span>
        )}
      </pre>
      <p className="mt-2 text-xs text-slate-500">
        From {request.sourceIp ?? "unknown"} at{" "}
        {formatDateTime(request.receivedAt)}
      </p>
    </div>
  </div>
);

/** Requests the mock received, refreshed every few seconds. */
export const RequestsPanel = ({ tokenUuid, canClear }: RequestsPanelProps) => {
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [live, setLive] = useState(true);
  const requests = useQuery({
    queryKey: mockKeys.requests(tokenUuid),
    queryFn: () => fetchMockRequests(tokenUuid),
    refetchInterval: live ? 3000 : false,
  });
  const clear = useMutation({
    mutationFn: () => clearRequests(tokenUuid),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: mockKeys.requests(tokenUuid),
      });
      void queryClient.invalidateQueries({ queryKey: mockKeys.all });
    },
  });
  const items = requests.data?.items ?? [];

  return (
    <section className="card">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-3">
        <h2 className="font-semibold">Requests received</h2>
        <span className="text-sm text-slate-500">
          {items.length === 200 ? "latest 200" : items.length}
        </span>
        <label className="ml-auto flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={live}
            onChange={(event) => setLive(event.target.checked)}
          />
          Live
        </label>
        {canClear ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={!items.length || clear.isLoading}
            onClick={() =>
              window.confirm("Delete every recorded request of this mock?") &&
              clear.mutate()
            }
          >
            Clear
          </Button>
        ) : null}
      </div>
      <ErrorBanner error={requests.error ?? clear.error} />
      {requests.isLoading ? (
        <Loading />
      ) : !items.length ? (
        <EmptyState title="No requests yet">
          Call one of the URLs above; requests show up here within a few
          seconds.
        </EmptyState>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((request) => (
            <li key={request.id}>
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-slate-50"
                onClick={() =>
                  setOpenId(openId === request.id ? null : request.id)
                }
                aria-expanded={openId === request.id}
              >
                <span className="w-16 shrink-0">
                  <Badge tone={methodTone(request.method)}>
                    {request.method}
                  </Badge>
                </span>
                <span className="min-w-0 flex-1 truncate font-mono text-xs">
                  {request.path}
                  {request.queryString ? (
                    <span className="text-slate-400">
                      ?{request.queryString}
                    </span>
                  ) : null}
                </span>
                {request.routeUuid ? null : (
                  <span className="text-xs text-amber-700">no route</span>
                )}
                <Badge tone={statusTone(request.responseStatus)}>
                  {request.responseStatus}
                </Badge>
                <span
                  className="w-28 shrink-0 text-right text-xs text-slate-500"
                  title={formatDateTime(request.receivedAt)}
                >
                  {formatRelative(request.receivedAt)}
                </span>
              </button>
              {openId === request.id ? (
                <RequestDetail request={request} />
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
